import React, { useState } from 'react';
import { Building2, User, Shield } from 'lucide-react';
import { User as UserType, Bank } from '../types/datamodel';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface LoginProps {
  onLogin: (user: UserType, selectedBank?: Bank) => void;
  availableBanks: Bank[];
}

export function Login({ onLogin, availableBanks }: LoginProps) {
  const [step, setStep] = useState<'login' | 'selectBank'>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [selectedBankId, setSelectedBankId] = useState('');
  const [tempUser, setTempUser] = useState<UserType | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Call login API to get real user data
      // For demo, we default to 'bank' role
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          password,
          role: 'bank'
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Login failed' }));
        alert('Login fallito: ' + (error.error || 'Errore sconosciuto'));
        return;
      }

      const data = await response.json();
      const user: UserType = {
        id: data.user.id,  // Use real ID from database
        username: data.user.username,
        role: data.user.role,
        createdAt: new Date()
      };

      // Tutti gli utenti vanno alla selezione banca
      setTempUser(user);
      setStep('selectBank');
    } catch (error) {
      console.error('Login error:', error);
      alert('Errore di connessione al server');
    }
  };

  const handleBankSelection = () => {
    if (tempUser && selectedBankId) {
      if (selectedBankId === 'all') {
        // Visualizza tutte le banche - nessuna banca specifica selezionata
        onLogin({ ...tempUser, bankId: undefined });
      } else {
        const selectedBank = availableBanks.find(b => b.id === selectedBankId);
        if (selectedBank) {
          onLogin({ ...tempUser, bankId: selectedBankId }, selectedBank);
        }
      }
    }
  };

  if (step === 'selectBank') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <Building2 className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Seleziona Banca Distributrice</h1>
            <p className="text-gray-600">Scegli la banca distributrice per cui effettuare i controlli</p>
          </div>

          <div className="space-y-4">
            {/* Opzione "Tutte le banche" */}
            <button
              onClick={() => setSelectedBankId('all')}
              className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                selectedBankId === 'all'
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-gray-200 hover:border-indigo-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800">Tutte le Banche</h3>
                  <p className="text-sm text-gray-600">Visualizza tutti i record di tutte le banche</p>
                </div>
                {selectedBankId === 'all' && (
                  <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>
            </button>

            {/* Lista delle banche */}
            {availableBanks.map(bank => (
              <button
                key={bank.id}
                onClick={() => setSelectedBankId(bank.id)}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  selectedBankId === bank.id
                    ? 'border-indigo-600 bg-indigo-50'
                    : 'border-gray-200 hover:border-indigo-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-800">{bank.name}</h3>
                    <p className="text-sm text-gray-600">Codice: {bank.code}</p>
                  </div>
                  {selectedBankId === bank.id && (
                    <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            ))}

            <button
              onClick={handleBankSelection}
              disabled={!selectedBankId}
              className="w-full mt-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Continua
            </button>

            <button
              onClick={() => {
                setStep('login');
                setTempUser(null);
              }}
              className="w-full py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Torna indietro
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <Shield className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Checklist Controlli</h1>
          <p className="text-lg text-indigo-600 font-semibold mb-1">AXA Insurance Company Demo</p>
          <p className="text-gray-600">Banche Distributrici - Sistema di Gestione</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Inserisci username"
                required
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Inserisci password"
              required
            />
          </div>

          <button
            type="submit"
            className="w-full py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg"
          >
            Accedi
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Credenziali demo: <span className="font-mono">demo / demo</span></p>
        </div>
      </div>
    </div>
  );
}
